import {
  faAngleDown,
  faAngleUp,
  faAnglesUpDown,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Select from '@radix-ui/react-select';
import React from 'react';
import { useCalculateMaximumMortgageCalculation } from '~/pages/Tools/CalculateMaximumMortgage/Calculate/context/CalculationContext';
import type { ProviderMatch } from '~/types/Provider';

const ProviderMatchesSelect: React.FC = () => {
  const { providerMatches, selected, handleSelect } =
    useCalculateMaximumMortgageCalculation();

  const SelectListItem = ({
    logoUrl,
    name,
    isBestDeal,
  }: ProviderMatch & { isBestDeal: boolean }) => (
    <div className="flex w-full gap-2.5">
      <div className="relative h-7 w-14 rounded-sm bg-white">
        <img
          src={logoUrl}
          alt={name}
          className="absolute inset-0 h-full w-full object-contain"
        />
      </div>
      <span className="font-bold leading-[28px]">{name}</span>
      {isBestDeal && (
        <span className="rounded-lg bg-blue-500 px-2 py-1 text-sm text-white">
          Beste deal
        </span>
      )}
    </div>
  );

  return (
    <div className="form-control col-span-2 xl:hidden">
      <label className="form-label" htmlFor="provider-match-select">
        Aanbieders op advieskeuze
      </label>

      <div className="relative">
        <Select.Root
          name="provider-match-select"
          value={selected?.slug}
          onValueChange={handleSelect}
        >
          <Select.Trigger className="form-input relative appearance-none !py-[0.75rem] !pr-10">
            <Select.Value>
              {selected ? (
                <SelectListItem
                  {...selected}
                  isBestDeal={selected?.slug === providerMatches[0]?.slug}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <span className="block h-7 w-14 rounded-sm bg-stone-100"></span>
                  <span className="font-bold leading-[28px]">
                    Aanbieders worden geladen...
                  </span>
                </div>
              )}
            </Select.Value>
            <Select.Icon className="accent-color pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm">
              <FontAwesomeIcon icon={faAnglesUpDown} />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              className="z-50 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-stone-200 bg-white shadow-lg"
              position="popper"
              sideOffset={5}
            >
              <Select.ScrollUpButton className="flex h-6 cursor-pointer items-center justify-center bg-white hover:bg-gray-50">
                <FontAwesomeIcon icon={faAngleUp} className="text-blue-500" />
              </Select.ScrollUpButton>

              <Select.Viewport className="max-h-[340px] p-1">
                {providerMatches.map((match, index) => (
                  <Select.Item
                    key={match.slug}
                    value={match.slug}
                    className="relative flex w-full cursor-pointer select-none items-center rounded px-8 py-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-[highlighted]:bg-gray-100"
                  >
                    <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-500"
                      />
                    </Select.ItemIndicator>

                    <Select.ItemText>
                      <SelectListItem {...match} isBestDeal={index === 0} />
                    </Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex h-6 cursor-pointer items-center justify-center bg-white hover:bg-gray-50">
                <FontAwesomeIcon icon={faAngleDown} className="text-blue-500" />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>
    </div>
  );
};

export default ProviderMatchesSelect;
